/**
 * OrganizationContext - Manages organization state and membership
 *
 * Provides:
 * - Current organization selection
 * - Organization list management
 * - Organization switching
 * - Membership checks
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
  useMemo,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  member_count?: number;
  current_user_role?: 'owner' | 'admin' | 'member' | 'viewer';
  is_demo?: boolean;
  /** When true, closing a finding without a fix needs a reason. Only the owner can change it. */
  require_finding_closure_reason?: boolean;
}

export interface OrganizationUpdateData {
  name?: string;
  require_finding_closure_reason?: boolean;
}

export interface OrganizationMember {
  id: string;
  organization_id: string;
  user_id: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  invited_by?: string;
  joined_at: string;
  user_email?: string;
  user_name?: string;
}

export interface OrganizationInvitation {
  id: string;
  organization_id: string;
  email: string;
  role: 'admin' | 'member' | 'viewer';
  invited_by: string;
  token: string;
  expires_at: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  created_at: string;
  organization_name?: string;
  inviter_name?: string;
  invite_url?: string;
}

export interface OrganizationContextType {
  // Organization state
  organizations: Organization[];
  currentOrganization: Organization | null;
  isLoading: boolean;
  error: string | null;
  hasOrganizations: boolean;
  hasRealOrganizations: boolean;
  onboardingComplete: boolean;

  // Organization actions
  refreshOrganizations: () => Promise<void>;
  switchOrganization: (organizationId: string) => void;
  createOrganization: (name: string, options?: { source?: 'manual' | 'onboarding' }) => Promise<Organization>;
  updateOrganization: (organizationId: string, data: OrganizationUpdateData) => Promise<Organization>;

  // Member actions
  getMembers: (organizationId: string) => Promise<OrganizationMember[]>;
  removeMember: (organizationId: string, userId: string) => Promise<void>;
  updateMemberRole: (organizationId: string, userId: string, role: string) => Promise<void>;

  // Invitation actions
  inviteMember: (organizationId: string, email: string, role: string, sendEmail?: boolean) => Promise<OrganizationInvitation>;
  getInvitations: (organizationId: string) => Promise<OrganizationInvitation[]>;
  revokeInvitation: (organizationId: string, invitationId: string) => Promise<void>;

  // Permission helpers
  isOwner: boolean;
  isAdmin: boolean;
  isViewer: boolean;
  canWrite: boolean;
  canManageMembers: boolean;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

const STORAGE_KEY = 'kolega_current_organization_id';

const storageKeyForUser = (userId: string) => `${STORAGE_KEY}:${userId}`;

interface OrganizationProviderProps {
  children: ReactNode;
}

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const api = useAuthenticatedApi();

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Render-time URL-org awareness — reactive to route changes including back button.
  // useLocation() re-renders this component whenever the URL changes, so urlOrg is
  // always current before any child context or component renders.
  const location = useLocation();
  const urlOrgId = new URLSearchParams(location.search).get('org');
  const selectableOrganizations = organizations;
  const urlOrg = useMemo(
    () => (urlOrgId ? selectableOrganizations.find((o) => o.id === urlOrgId) ?? null : null),
    [urlOrgId, selectableOrganizations]
  );
  const effectiveOrganization = urlOrg ?? currentOrganization;
  const userScopedStorageKey = user?.id ? storageKeyForUser(user.id) : null;

  // Sync URL-param org to state/localStorage so navigating away preserves the correct org.
  useEffect(() => {
    if (urlOrg && urlOrg.id !== currentOrganization?.id) {
      setCurrentOrganization(urlOrg);
    }
    if (urlOrg && userScopedStorageKey) {
      localStorage.setItem(userScopedStorageKey, urlOrg.id);
    }
  }, [urlOrg, currentOrganization?.id, userScopedStorageKey]);

  // Fetch organizations
  const refreshOrganizations = useCallback(async () => {
    if (!isAuthenticated) {
      setOrganizations([]);
      setCurrentOrganization(null);
      setOnboardingComplete(false);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await api.get('/organizations');
      const orgs: Organization[] = response.organizations || [];
      setOrganizations(orgs);
      setOnboardingComplete(Boolean(response.onboarding_complete));

      // Prefer ?org= URL param (deep links from PR/MR comments) over saved state.
      const urlOrgId = new URLSearchParams(window.location.search).get('org');
      const urlOrg = urlOrgId ? orgs.find((o) => o.id === urlOrgId) ?? null : null;
      const scopedSavedOrgId = userScopedStorageKey ? localStorage.getItem(userScopedStorageKey) : null;
      const scopedSavedOrg = scopedSavedOrgId ? orgs.find((o) => o.id === scopedSavedOrgId) ?? null : null;

      const legacySavedOrgId = localStorage.getItem(STORAGE_KEY);
      const legacySavedOrg = !scopedSavedOrgId && legacySavedOrgId
        ? orgs.find((o) => o.id === legacySavedOrgId && !o.is_demo) ?? null
        : null;
      const firstRealOrg = orgs.find((o) => !o.is_demo) ?? null;
      const firstPrivateDemoOrg = orgs.find((o) => o.is_demo) ?? null;
      const selectedOrg = urlOrg ?? scopedSavedOrg ?? legacySavedOrg ?? firstRealOrg ?? firstPrivateDemoOrg;

      if (selectedOrg) {
        setCurrentOrganization(selectedOrg);
        if (userScopedStorageKey) {
          localStorage.setItem(userScopedStorageKey, selectedOrg.id);
        }
        if (legacySavedOrgId) {
          localStorage.removeItem(STORAGE_KEY);
        }
      } else {
        setCurrentOrganization(null);
        if (userScopedStorageKey) {
          localStorage.removeItem(userScopedStorageKey);
        }
        if (legacySavedOrgId) {
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch organizations:', err);
      setError(err.data?.detail || err.message || 'Failed to load organizations');
      setOrganizations([]);
      setCurrentOrganization(null);
      setOnboardingComplete(false);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, api, userScopedStorageKey]);

  // Load organizations on auth change
  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (isAuthenticated && !user?.id) {
      return;
    }

    refreshOrganizations();
  }, [authLoading, isAuthenticated, user?.id, refreshOrganizations]);

  // Switch organization
  const switchOrganization = useCallback((organizationId: string) => {
    const org = organizations.find((o) => o.id === organizationId);
    if (org) {
      setCurrentOrganization(org);
      if (userScopedStorageKey) {
        localStorage.setItem(userScopedStorageKey, organizationId);
      }
    }
  }, [organizations, userScopedStorageKey]);

  // Create organization
  const createOrganization = useCallback(
    async (name: string, options?: { source?: 'manual' | 'onboarding' }): Promise<Organization> => {
      const payload = options?.source ? { name, source: options.source } : { name };
      const newOrg: Organization = await api.post('/organizations', payload);

      setOrganizations((prev) => [...prev, newOrg]);
      setCurrentOrganization(newOrg);
      if (userScopedStorageKey) {
        localStorage.setItem(userScopedStorageKey, newOrg.id);
      }

      return newOrg;
    },
    [api, userScopedStorageKey]
  );

  // Update organization
  const updateOrganization = useCallback(async (
    organizationId: string,
    data: OrganizationUpdateData
  ): Promise<Organization> => {
    const updatedOrg: Organization = await api.put(`/organizations/${organizationId}`, data);

    setOrganizations((prev) =>
      prev.map((org) => (org.id === organizationId ? updatedOrg : org))
    );

    if (currentOrganization?.id === organizationId) {
      setCurrentOrganization(updatedOrg);
    }

    return updatedOrg;
  }, [api, currentOrganization?.id]);

  // Member actions
  const getMembers = useCallback(async (organizationId: string): Promise<OrganizationMember[]> => {
    const response = await api.get(`/organizations/${organizationId}/members`);
    return response.members || [];
  }, [api]);

  const removeMember = useCallback(async (organizationId: string, userId: string): Promise<void> => {
    await api.delete(`/organizations/${organizationId}/members/${userId}`);
  }, [api]);

  const updateMemberRole = useCallback(async (
    organizationId: string,
    userId: string,
    role: string
  ): Promise<void> => {
    await api.put(`/organizations/${organizationId}/members/${userId}/role?role=${role}`);
  }, [api]);

  // Invitation actions
  const inviteMember = useCallback(async (
    organizationId: string,
    email: string,
    role: string,
    sendEmail: boolean = true
  ): Promise<OrganizationInvitation> => {
    return await api.post(`/organizations/${organizationId}/invitations`, { email, role, send_email: sendEmail });
  }, [api]);

  const getInvitations = useCallback(async (organizationId: string): Promise<OrganizationInvitation[]> => {
    const response = await api.get(`/organizations/${organizationId}/invitations`);
    return response || [];
  }, [api]);

  const revokeInvitation = useCallback(async (
    organizationId: string,
    invitationId: string
  ): Promise<void> => {
    await api.delete(`/organizations/${organizationId}/invitations/${invitationId}`);
  }, [api]);

  // Permission helpers
  const isOwner = useMemo(() => effectiveOrganization?.current_user_role === 'owner', [effectiveOrganization]);

  const isAdmin = useMemo(() => {
    const role = effectiveOrganization?.current_user_role;
    return role === 'owner' || role === 'admin';
  }, [effectiveOrganization]);

  const isViewer = useMemo(() => effectiveOrganization?.current_user_role === 'viewer', [effectiveOrganization]);

  const canWrite = useMemo(() => {
    const role = effectiveOrganization?.current_user_role;
    return role === 'owner' || role === 'admin' || role === 'member';
  }, [effectiveOrganization]);

  const canManageMembers = isAdmin;

  const hasOrganizations = organizations.length > 0;
  const hasRealOrganizations = organizations.some((org) => !org.is_demo);

  const value = useMemo<OrganizationContextType>(() => ({
    organizations,
    currentOrganization: effectiveOrganization,
    isLoading,
    error,
    hasOrganizations,
    hasRealOrganizations,
    onboardingComplete,

    refreshOrganizations,
    switchOrganization,
    createOrganization,
    updateOrganization,

    getMembers,
    removeMember,
    updateMemberRole,

    inviteMember,
    getInvitations,
    revokeInvitation,

    isOwner,
    isAdmin,
    isViewer,
    canWrite,
    canManageMembers,
  }), [
    organizations,
    effectiveOrganization,
    isLoading,
    error,
    hasOrganizations,
    hasRealOrganizations,
    onboardingComplete,
    refreshOrganizations,
    switchOrganization,
    createOrganization,
    updateOrganization,
    getMembers,
    removeMember,
    updateMemberRole,
    inviteMember,
    getInvitations,
    revokeInvitation,
    isOwner,
    isAdmin,
    isViewer,
    canWrite,
    canManageMembers,
  ]);

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within OrganizationProvider');
  }
  return context;
};

export default OrganizationContext;
